import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { FuseLegacyConfigService } from "@nx-workspace/fuse";
import { FuseSidebarService } from "@fuse/components/sidebar/sidebar.service";
import { navigation } from "app/navigation/navigation";
import { AppAuthService } from "@shared/auth/app-auth.service";
import { FuseProgressBarService } from "@fuse/components/progress-bar/progress-bar.service";
import { AppSessionService } from "@vadmin/account/auth";
import { TranslationService } from "@shared/translation.service";
import { TenantLoginInfoDto } from "@api/core.models";
import { AppConsts } from "@shared/AppConsts";
import { FuseSplashScreenService } from "@fuse/services/splash-screen.service";
import { NotificationService, NotificationType } from "@app/wapps/notification/notification.service";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { Notification } from "@app/wapps/notification/notification.model";
import { MatMenu, MatMenuTrigger } from "@angular/material/menu";
import { UsersService } from "@app/wapps/system/users/users.service";
import { DomSanitizer, SafeUrl } from "@angular/platform-browser";
import { SupportChatService } from "@app/wapps/system/support/support-chat.service";
import { ChatPanelService } from "app/wapps/chat-panel/chat-panel.service";
import { environment } from "../../../../environments/environment";
import { brandAsset } from "@i18n";
import { MigrationConsentService } from "@app/wapps/data-migration/migration-consent.service";

@Component({
    selector: "toolbar",
    templateUrl: "./toolbar.component.html",
    styleUrls: ["./toolbar.component.scss"],
})
export class ToolbarComponent implements OnInit, OnDestroy {
    horizontalNavbar: boolean;

    rightNavbar: boolean;

    hiddenNavbar: boolean;

    navigation: any;

    skipCount: number = 0;

    maxResult: number = 5;

    @ViewChild("profileMenu")
    profileMenu: MatMenu;

    @ViewChild("notificationMenu") notificationMenu: MatMenu;

    showItemProfileInfo: boolean = true;

    // Private
    private _unsubscribeAll: Subject<any>;

    public notifications: Notification[] = [];

    countUnread: number = 0;

    @ViewChild(MatMenuTrigger) trigger: MatMenuTrigger;

    public avatarUrl: string;

    public username: string;

    public fullName: string;

    public logoUrl: SafeUrl;

    supportChatEnabled: boolean;

    chatEnabled: boolean;

    public tenantId: number;

    tenantClasses: string[] = [];

    readonly migrationConsent = inject(MigrationConsentService);

    readonly appSession = inject(AppSessionService);

    private readonly _domSanitizer = inject(DomSanitizer);

    private readonly _changeDetector = inject(ChangeDetectorRef);

    private readonly _translationService = inject(TranslationService);

    constructor(
        private _authService: AppAuthService,
        private _fuseConfigService: FuseLegacyConfigService,
        private _fuseSidebarService: FuseSidebarService,
        private _internalChatService: ChatPanelService,
        private _fuseProgressBarService: FuseProgressBarService,
        private _notificationService: NotificationService,
        public _matDialog: MatDialog,
        private _router: Router,
        private _fuseSplashService: FuseSplashScreenService,
        private _usersService: UsersService,
        public _supportChatService: SupportChatService
    ) {
        this.username = this.appSession.user != null ? this.appSession.user.name : "";
        this.fullName =
            this.appSession.user != null ? this.appSession.user.name + " " + this.appSession.user.surname : "";
        let avatar = this.appSession.user != null ? this.appSession.user.avatar : undefined;
        this.avatarUrl = !!avatar ? avatar : "assets/images/avatars/profile.jpg";

        this.navigation = navigation;

        // Set the private defaults
        this._unsubscribeAll = new Subject();

        this._usersService.onLoggedUserAvatarChanged.pipe(takeUntil(this._unsubscribeAll)).subscribe((url) => {
            if (url) {
                this.avatarUrl = url;
            }
        });

        this._usersService.onLoggedUserChanged.pipe(takeUntil(this._unsubscribeAll)).subscribe((user) => {
            if (user) {
                this.username = user.name;
                this.fullName = user.fullName;
            }
        });

        this.appSession.onTenantInfoChanged.pipe(takeUntil(this._unsubscribeAll)).subscribe((val) => {
            if (this.appSession.tenantInfo && this.appSession.tenantInfo.logoUrl) {
                this.logoUrl = this._domSanitizer.bypassSecurityTrustUrl(this.appSession.tenantInfo.logoUrl);
            } else {
                this.logoUrl = this._domSanitizer.bypassSecurityTrustUrl(brandAsset("mark"));
            }
        });

        this.supportChatEnabled = this._supportChatService.enabled;

        this._internalChatService.chatEnabled.pipe(takeUntil(this._unsubscribeAll)).subscribe((chatEnabled) => {
            this.chatEnabled = chatEnabled;
        });
    }

    ngOnInit(): void {
        // Subscribe to the config changes
        this._fuseConfigService.config.pipe(takeUntil(this._unsubscribeAll)).subscribe((settings) => {
            this.horizontalNavbar = settings.layout.navbar.position === "top";
            this.rightNavbar = settings.layout.navbar.position === "right";
            this.hiddenNavbar = settings.layout.navbar.hidden === true;
            this.showItemProfileInfo = settings.layout.toolbar.showItemProfileInfo === true;
        });

        //Notifications
        this._notificationService.onRecentNotificationsChanged
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((notifications: any[]) => {
                this.notifications = notifications;
                this._changeDetector.markForCheck();
            });

        this._notificationService.onBadgeUpdated
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((badgeCount: number) => {
                this.countUnread = badgeCount;
            });

        this._notificationService.onNotificationOpened
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((value: boolean) => {
                if (value) {
                    this.notificationMenu.closed.emit();
                }
            });
        this.appSession.onSessionChanged.pipe(takeUntil(this._unsubscribeAll)).subscribe((user) => {
            if (!!user) {
                this.username = user != null ? user.name : "";
                this.fullName = user != null ? user.name + " " + user.surname : "";
            }
        });

        this.tenantId = this.appSession.tenantId;

        this.tenantClasses = [`tenant-${environment.production ? this.tenantId : ""}`];
    }

    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    l(key: string, ...args: any[]): string {
        return this._translationService.l(key, ...args);
    }

    isCalendar(notification) {
        return NotificationType.Appointment == notification.notification.data.type;
    }

    getDateString(date) {
        return this._notificationService.getDate(new Date(date));
    }

    setReadAll() {
        this._notificationService.setReadAll().then();
    }

    loadRecentNotifications() {
        this._notificationService.reloadRecentNotifications();
    }

    openNotification(event, notification) {
        this._notificationService.handleOpenNotification(notification);
        this.trigger.closeMenu();
    }

    openMigrationConsent(): void {
        this.migrationConsent.open();
        this.trigger.closeMenu();
    }

    markAsRead(event, id) {
        event.stopPropagation();
        this._notificationService.setRead(id).then();
    }

    markAllNotificationsAsRead() {
        this._notificationService.setReadAll().then();
    }

    showAllNotifications() {
        this._router.navigate(["notification"]).then();
        this.notificationMenu.closed.emit();
    }

    /**
     * Toggle sidebar open
     *
     * @param key
     */
    toggleSidebarOpen(key): void {
        this._fuseSidebarService.getSidebar(key).toggleOpen();
        this.profileMenu.closed.emit();
    }

    /**
     * Search
     *
     * @param value
     */
    search(value): void {
        // Do your search here...
    }

    logout(): void {
        this.profileMenu.closed.emit();
        this._fuseProgressBarService.show();
        this._authService.logout(true);
    }

    get canReturnToImpersonate(): boolean {
        return this.appSession.isImpersonating();
    }

    returnToTenants(): void {
        this._fuseSplashService.show();
        this._authService.backToImpersonator();
    }

    navigateToProfilePage() {
        this.profileMenu.closed.emit();
    }

    navigateToNotificationSettings() {
        this._router.navigate(["notification/settings"]).then();
        this.notificationMenu.closed.emit();
    }

    changeTenant(info: TenantLoginInfoDto) {
        this.profileMenu.closed.emit();
        this._fuseSplashService.show();
        this.appSession.changeTenant(info).then(() => {
            this._fuseSplashService.hide();
        });
    }

    openLegalDocuments() {
        this._router.navigate(["legal"]).then();
        this.profileMenu.closed.emit();
    }

    // Legacy (v1) only: v2 already centralizes subscriptions under /settings.
    get isLegacyVersion(): boolean {
        return abp.setting.get(AppConsts.Settings.ApplicationVersion) !== "2.0";
    }

    get isAdmin(): boolean {
        return this.appSession.isUserInRoleTenant(AppConsts.RolesNames.Admin);
    }

    openPlansAndSubscriptions() {
        this._router.navigate(["/subscription/plans"]).then();
        this.profileMenu.closed.emit();
    }

    isLocked() {
        return this.appSession.isLocked();
    }

    openKb() {
        window.open("https://help.vsaude.com.br", "_blank");
    }

    openCanpaign() {
        window.open("https://vsaude.com.br/performance/euindicovsaude/", "_blank");
    }

    openChat() {
        this._supportChatService.showMessengerPanel();
    }
}
